import React, { useMemo } from 'react';
import { Box, IconButton, Typography, useMediaQuery, useTheme, type SxProps, type Theme } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { getChartTokens } from '../../theme/chartTheme';
import { numberLocale, type MetricValueKind } from '../../utils/numberFormat';
import ChartPanel from '../charts/ChartPanel';
import CompositionList from '../charts/CompositionList';
import AnimatedFigure from '../metrics/AnimatedFigure';
import EmptyState from '../EmptyState';

interface ChartCardProps {
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  type: 'bar' | 'line' | 'area' | 'pie';
  /** Vertical columns (default) or horizontal bars. Only applies when type is bar. */
  barLayout?: 'vertical' | 'horizontal';
  dataKey: string;
  nameKey: string;
  /**
   * Optional category key: rows sharing the same `nameKey` are pivoted into one
   * stacked series per category (e.g. incoming waste per date × waste type).
   */
  seriesKey?: string;
  /** Legend / tooltip label for a single series (defaults to the chart title). */
  seriesLabel?: string;
  /** How values are formatted (axis, tooltip, list). */
  valueKind?: MetricValueKind;
  /** Unit label shown in the caption / list (e.g. "kg"). */
  unit?: string;
  caption?: React.ReactNode;
  icon?: SvgIconComponent;
  footer?: React.ReactNode;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Hide the top-right menu button. */
  hideMenu?: boolean;
  /** Chart plot height in px (default 280). */
  chartMinHeight?: number;
  /** When true, the panel stretches with its parent. */
  fillPaper?: boolean;
  /** Extra styles for the panel. */
  paperSx?: SxProps<Theme>;
  /** Segment colours for pies / per-bar colours (defaults to the content chart palette). */
  segmentColors?: string[];
  /** Row key holding a secondary caption for composition lists (e.g. EF version). */
  metaKey?: string;
  id?: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/**
 * Institutional chart panel (recharts): hairline horizontal grid, no axis lines,
 * thin strokes, muted palette, locale-aware ticks. The SVG is kept LTR so axes
 * read left-to-right in every language; titles and legends follow the page.
 */
const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  type,
  barLayout = 'vertical',
  dataKey,
  nameKey,
  seriesKey,
  seriesLabel,
  valueKind = 'decimal',
  unit,
  caption,
  icon,
  footer,
  onMenuClick,
  hideMenu = false,
  chartMinHeight,
  fillPaper = false,
  paperSx,
  segmentColors,
  metaKey,
  id,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const chartH = chartMinHeight ?? 280;
  // Compact ticks are longer in non-Latin locales ('1.5 مليون'); give the value axis room.
  const valueAxisWidth = (i18n.language || 'en').startsWith('en') ? 48 : 76;
  const palette = segmentColors?.length ? segmentColors : tokens.palette;
  const singleLabel = seriesLabel ?? title;

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(numberLocale(i18n.language), { month: 'short', day: 'numeric' }),
    [i18n.language],
  );

  const formatCategory = (value: unknown) => {
    const text = String(value ?? '');
    if (ISO_DATE.test(text)) {
      const d = new Date(text);
      if (!Number.isNaN(d.getTime())) return dateFormatter.format(d);
    }
    return text;
  };
  const formatValue = (value: number) => fmt.withUnit(valueKind, value);
  const formatTick = (value: number) => fmt.compact(value);

  // Pivot long rows into one stacked series per category when requested.
  const { rows, series } = useMemo(() => {
    if (!seriesKey) {
      return { rows: data ?? [], series: [{ key: dataKey, label: singleLabel }] };
    }
    const byName = new Map<string, Record<string, unknown>>();
    const keys: string[] = [];
    for (const row of data ?? []) {
      const name = String(row?.[nameKey] ?? '');
      const category = String(row?.[seriesKey] ?? t('carbonAvoidance.other'));
      if (!keys.includes(category)) keys.push(category);
      const bucket = byName.get(name) ?? { [nameKey]: name };
      bucket[category] = Number(bucket[category] ?? 0) + Number(row?.[dataKey] ?? 0);
      byName.set(name, bucket);
    }
    return {
      rows: [...byName.values()],
      series: keys.map((key) => ({ key, label: key })),
    };
  }, [data, dataKey, nameKey, seriesKey, singleLabel, t]);

  const isEmpty = !rows || rows.length === 0;

  const tooltip = (
    <Tooltip
      {...tokens.tooltip}
      labelFormatter={(label) => formatCategory(label)}
      formatter={(value: number, name: string) => [formatValue(Number(value)), name]}
    />
  );
  const legend =
    series.length > 1 ? (
      <Legend
        wrapperStyle={tokens.legend.wrapperStyle}
        iconSize={tokens.legend.iconSize}
        iconType="square"
        formatter={(value: string) => (
          <span style={{ color: theme.palette.text.secondary, fontSize: 12 }}>{value}</span>
        )}
      />
    ) : null;
  const grid = <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />;
  const xAxis = (
    <XAxis
      dataKey={nameKey}
      tickFormatter={formatCategory}
      tick={tokens.axis.tick}
      axisLine={false}
      tickLine={false}
      tickMargin={tokens.axis.tickMargin}
      minTickGap={16}
    />
  );
  const yAxis = (
    <YAxis
      tickFormatter={formatTick}
      tick={tokens.axis.tick}
      axisLine={false}
      tickLine={false}
      width={valueAxisWidth}
    />
  );
  const margin = { top: 8, right: 8, left: 0, bottom: 0 };

  const renderComposition = () => {
    const listRows = rows.map((row, index) => ({
      id: String(row?.[nameKey] ?? index),
      label: formatCategory(row?.[nameKey]),
      value: Number(row?.[dataKey] ?? 0),
      color: palette[index % palette.length],
      meta: metaKey && row?.[metaKey] ? String(row[metaKey]) : undefined,
    }));
    const useDonut = listRows.length <= 5 && !isNarrow;
    if (!useDonut) {
      return (
        <CompositionList
          rows={listRows}
          formatValue={(v) => fmt.metric(valueKind, v).text}
          unit={unit ?? fmt.metric(valueKind, 0).unit}
        />
      );
    }
    const total = listRows.reduce((acc, r) => acc + Math.max(r.value, 0), 0);
    const totalFigure = fmt.metric(valueKind, total);
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '200px minmax(0, 1fr)' }, gap: 3, alignItems: 'center' }}>
        <Box dir="ltr" sx={{ position: 'relative', height: 200, minWidth: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                {...tokens.animation}
                data={listRows}
                dataKey="value"
                nameKey="label"
                innerRadius={tokens.pie.innerRadius}
                outerRadius={tokens.pie.outerRadius}
                paddingAngle={tokens.pie.paddingAngle}
                stroke={theme.palette.background.paper}
                strokeWidth={1}
              >
                {listRows.map((r) => (
                  <Cell key={r.id} fill={r.color} />
                ))}
              </Pie>
              {tooltip}
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: '1.125rem', fontVariantNumeric: 'tabular-nums' }}>
              <bdi><AnimatedFigure value={totalFigure.text} /></bdi>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {unit ?? totalFigure.unit}
            </Typography>
          </Box>
        </Box>
        <CompositionList
          rows={listRows}
          formatValue={(v) => fmt.metric(valueKind, v).text}
          unit={unit ?? totalFigure.unit}
          swatches
          dense
        />
      </Box>
    );
  };

  const renderChart = () => {
    if (type === 'pie') return renderComposition();

    const height = fillPaper ? '100%' : chartH;
    let chart: React.ReactElement;
    if (type === 'bar' && barLayout === 'horizontal') {
      chart = (
        <BarChart layout="vertical" data={rows} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid stroke={tokens.grid.stroke} horizontal={false} />
          <XAxis type="number" tickFormatter={formatTick} tick={tokens.axis.tick} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey={nameKey}
            width={112}
            tick={tokens.axis.tick}
            axisLine={false}
            tickLine={false}
            tickFormatter={formatCategory}
          />
          {tooltip}
          <Bar {...tokens.animation} dataKey={dataKey} name={singleLabel} maxBarSize={tokens.bar.maxBarSize} radius={[0, 2, 2, 0]}>
            {rows.map((_, index) => (
              <Cell key={`bar-cell-${index}`} fill={palette[index % palette.length]} />
            ))}
          </Bar>
        </BarChart>
      );
    } else if (type === 'bar') {
      chart = (
        <BarChart data={rows} margin={margin}>
          {grid}
          {xAxis}
          {yAxis}
          {tooltip}
          {legend}
          {series.map((s, i) => (
            <Bar
              {...tokens.animation}
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId={seriesKey ? 'stack' : undefined}
              fill={series.length > 1 ? tokens.seriesColor(i) : theme.palette.primary.main}
              maxBarSize={tokens.bar.maxBarSize}
              radius={i === series.length - 1 ? tokens.bar.radius : [0, 0, 0, 0]}
            />
          ))}
        </BarChart>
      );
    } else if (type === 'line') {
      chart = (
        <LineChart data={rows} margin={margin}>
          {grid}
          {xAxis}
          {yAxis}
          {tooltip}
          {legend}
          {series.map((s, i) => (
            <Line
              {...tokens.animation}
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={series.length > 1 ? tokens.seriesColor(i) : theme.palette.primary.main}
              strokeWidth={tokens.line.strokeWidth}
              dot={tokens.line.dot}
              activeDot={tokens.line.activeDot}
            />
          ))}
        </LineChart>
      );
    } else {
      chart = (
        <AreaChart data={rows} margin={margin}>
          {grid}
          {xAxis}
          {yAxis}
          {tooltip}
          {legend}
          {series.map((s, i) => {
            const color = series.length > 1 ? tokens.seriesColor(i) : theme.palette.primary.main;
            return (
              <Area
                {...tokens.animation}
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stackId={seriesKey ? 'stack' : undefined}
                stroke={color}
                fill={color}
                strokeWidth={tokens.area.strokeWidth}
                fillOpacity={tokens.area.fillOpacity}
              />
            );
          })}
        </AreaChart>
      );
    }

    return (
      <Box dir="ltr" sx={{ height, minHeight: fillPaper ? chartH : undefined, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          {chart}
        </ResponsiveContainer>
      </Box>
    );
  };

  return (
    <ChartPanel
      id={id}
      title={title}
      icon={icon}
      caption={caption ?? (unit ? unit : undefined)}
      footer={footer}
      sx={[
        { height: fillPaper ? '100%' : 'auto' },
        ...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
      ]}
      actions={
        !hideMenu && onMenuClick ? (
          <IconButton size="small" onClick={onMenuClick} aria-label={t('common.actions')} sx={{ color: 'text.secondary' }}>
            <MoreVertRounded fontSize="small" />
          </IconButton>
        ) : null
      }
    >
      {isEmpty ? (
        <EmptyState title={t('common.noData')} variant="inline" minHeight={chartH} />
      ) : (
        renderChart()
      )}
    </ChartPanel>
  );
};

export default ChartCard;
